import React, { useRef } from 'react'
import {
  Column,
  ColumnChooser,
  ColumnChooserSearch,
  DataGrid,
  DataGridRef,
  DataGridTypes,
  Editing,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Paging,
  PatternRule,
  RangeRule,
  RequiredRule,
  Scrolling,
  SearchPanel,
  Sorting,
  StateStoring,
} from 'devextreme-react/data-grid'
import ODataStore from 'devextreme/data/odata/store'
import { useTokenRefresh } from '@/auth/azure/azureManager'
import { useScreenSize } from '@/themes/media-query'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { ad_percentuale } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  applyPendingChanges,
  notifySuccess,
  storeErrorHandler,
  validateBeforeSaving,
} from '@/routes/admin/autodesk/autodesk.utils'

export const Percentuali = () => {
  const token = useTokenRefresh()
  const { service, client } = useQsAdminApiManager()
  const dataGridRef = useRef<DataGridRef>(null)
  const currentScreenSize = useScreenSize()

  const percentualiDataSource = {
    store: new ODataStore({
      url: `${import.meta.env.VITE_QSADMINAPI_HOST}/ad_percentuale`,
      key: 'id',
      keyType: 'Int32',
      version: 4,
      errorHandler: storeErrorHandler,
      beforeSend: ODataStoreRequestConfiguration(token),
      deserializeDates: false,
    }),
    expand: ['commissioni'],
    requireTotalCount: true,
  }

  // La percentuale viene confrontata con quella del CSV Autodesk nell'export commissioni: deve essere univoca
  const findDuplicatePercentuale = async (changes: DataGridTypes.DataChange[]) => {
    const response = await client.get(`${service.getPath()}/ad_percentuale?$select=id,percentuale`)
    const rows = applyPendingChanges<ad_percentuale>(response.data.value, changes)
    for (const row of rows.filter((r) => r.touched)) {
      const percentuale = row.data.percentuale
      if (percentuale === undefined || percentuale === null) continue
      if (rows.filter((r) => r.data.percentuale === percentuale).length > 1)
        return `La percentuale ${percentuale}% esiste già`
    }
  }

  return (
    <div className="management-grid content-block">
      <h2>Percentuali Autodesk</h2>

      <DataGrid
        dataSource={percentualiDataSource}
        height="75vh"
        className={currentScreenSize.isSmall ? 'grid-height-small' : 'grid-height-default'}
        noDataText="Nessuna percentuale trovata"
        showBorders={false}
        remoteOperations={{
          filtering: true,
          sorting: true,
          paging: true,
        }}
        showColumnLines={true}
        focusedRowEnabled={false}
        columnHidingEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
        allowColumnResizing={currentScreenSize.isLarge || currentScreenSize.isMedium}
        allowColumnReordering={currentScreenSize.isLarge || currentScreenSize.isMedium}
        rowAlternationEnabled={true}
        wordWrapEnabled={true}
        onSaving={(e) => validateBeforeSaving(e, () => findDuplicatePercentuale(e.changes))}
        onRowInserted={() => notifySuccess('Created')}
        onRowUpdated={() => notifySuccess('Updated')}
        onRowRemoved={() => notifySuccess('Deleted')}
        ref={dataGridRef}
      >
        <Editing
          mode="batch"
          allowAdding={true}
          allowUpdating={true}
          // Una percentuale usata da almeno una commissione non può essere eliminata
          allowDeleting={(e: { row?: DataGridTypes.Row }) => !e.row?.data?.commissioni?.length}
          useIcons={true}
        />

        <StateStoring enabled={true} type="localStorage" storageKey="ad-percentuali-grid" savingTimeout={50} />

        <HeaderFilter visible />
        <FilterPanel visible />
        <FilterRow visible />
        <SearchPanel visible width={currentScreenSize.isXSmall ? 200 : 250} />
        <Scrolling mode="virtual" />
        <Sorting mode="multiple" />
        <Export enabled />
        <ColumnChooser enabled>
          <ColumnChooserSearch enabled />
        </ColumnChooser>

        <Paging defaultPageSize={20} enabled />

        <Column dataField="id" caption="ID" dataType="number" width={80} allowEditing={false} visible={false} />

        <Column
          dataField="percentuale"
          caption="Percentuale (%)"
          dataType="number"
          defaultSortOrder="asc"
          width={150}
          editorOptions={{ format: '#0', showSpinButtons: true, min: 1, max: 100 }}
        >
          <RequiredRule message="Percentuale obbligatoria" />
          <PatternRule pattern={/^\d+$/} message="La percentuale deve essere un numero intero" />
          <RangeRule min={1} max={100} message="La percentuale deve essere compresa tra 1 e 100" />
        </Column>

        <Column dataField="note" caption="Note" dataType="string" hidingPriority={1} />

        <Column
          caption="Commissioni"
          dataType="number"
          allowEditing={false}
          allowSorting={false}
          allowFiltering={false}
          width={130}
          calculateCellValue={(rowData: ad_percentuale) => rowData.commissioni?.length ?? 0}
          hidingPriority={2}
        />
      </DataGrid>
    </div>
  )
}
